/**
 * useSpherePolling Hook
 *
 * Shared polling logic for refreshing sphere data after mutations.
 * Used by Atlas, TreeMap, and other components that need to catch
 * cascading updates (status rollups, flow deployments, etc.)
 *
 * Pattern: Bounded polling with recursive setTimeout
 * - Default: 3 attempts at 5-second intervals (15 seconds total)
 * - Only polls visible nodes to minimize API calls
 * - Uses skipCache=true to bypass Redis cache
 */

import { useCallback, useRef } from 'react';
import { isTransientReadFailure } from '../utils/isTransientReadFailure';

export interface SpherePollingOptions {
  /** Maximum number of polling attempts (default: 3) */
  maxAttempts?: number;
  /** Interval between polls in milliseconds (default: 5000) */
  intervalMs?: number;
}

export interface UseSpherePollingParams {
  /** API client with get method for fetching spheres */
  apiClient: { get: <T>(url: string) => Promise<T> } | null | undefined;

  /**
   * Callback when fresh sphere data is received.
   * Component is responsible for merging into its own state.
   */
  onSphereUpdate: (sphere: any) => void;

  /**
   * Optional function to check if a sphere is currently visible.
   * If provided, only visible spheres will be polled.
   * If not provided, all requested spheres will be polled.
   */
  isNodeVisible?: (sphereId: string) => boolean;

  /**
   * Include assigned perspectives in poll response.
   * Used by Atlas for perspective display.
   */
  includeAssignedPerspectives?: boolean;

  /**
   * Called when a `refreshSpheres` read of a sphere is refused (403, 404, or any failure that is not
   * transient), so the host can read its view the normal way: a sphere the person can no longer see
   * must not stay on screen (spec D71). A transient failure never calls it.
   */
  onSphereRefused?: (sphereId: string) => void;
}

export interface UseSpherePollingReturn {
  /**
   * Start polling for sphere updates.
   *
   * @param sphereIds - Array of sphere IDs to poll
   * @param alwaysPollIds - IDs to always poll regardless of visibility (e.g., newly created spheres)
   * @param options - Optional polling configuration
   */
  pollForUpdates: (
    sphereIds: string[],
    alwaysPollIds?: string[],
    options?: SpherePollingOptions
  ) => void;

  /**
   * Re-read each sphere once, now, for a change that is already stored (a new glyph, colour or
   * picture), so there is no cascade to wait for. Leaves any running poll alone. A read that fails
   * transiently keeps the sphere as it is on screen; a refused read is reported to `onSphereRefused`.
   */
  refreshSpheres: (sphereIds: string[]) => Promise<void>;

  /**
   * Cancel any in-progress polling
   */
  cancelPolling: () => void;
}

const DEFAULT_MAX_ATTEMPTS = 3;
const DEFAULT_INTERVAL_MS = 5000;

/**
 * Hook for polling sphere data after mutations to catch cascading updates.
 *
 * @example
 * ```typescript
 * const { pollForUpdates } = useSpherePolling({
 *   apiClient: graphClient,
 *   onSphereUpdate: (sphere) => updateSphereInState(sphere),
 *   isNodeVisible: (id) => visibleSphereIds.has(id),
 * });
 *
 * // After creating a sphere:
 * pollForUpdates([newSphere.id, parentId, ...ancestorIds], [newSphere.id]);
 * ```
 */
export function useSpherePolling({
  apiClient,
  onSphereUpdate,
  isNodeVisible,
  includeAssignedPerspectives = false,
  onSphereRefused,
}: UseSpherePollingParams): UseSpherePollingReturn {
  // Track active polling timeout for cancellation
  const pollingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isPollingRef = useRef(false);

  const cancelPolling = useCallback(() => {
    if (pollingTimeoutRef.current) {
      clearTimeout(pollingTimeoutRef.current);
      pollingTimeoutRef.current = null;
    }
    isPollingRef.current = false;
  }, []);

  /**
   * One fresh read of a sphere (skipCache, with status and flow). A failure is logged and skipped;
   * the result says whether it was a refusal rather than a transient failure.
   */
  const readSphere = useCallback(
    async (sphereId: string): Promise<'read' | 'transient' | 'refused'> => {
      if (!apiClient) return 'transient';
      try {
        const queryParams = new URLSearchParams({
          skipCache: 'true',
          getStatus: 'true',
          expandFlow: 'true',
        });

        if (includeAssignedPerspectives) {
          queryParams.set('getAssignedPerspectives', 'true');
        }

        const response = await apiClient.get<{ sphere: any }>(
          `/spheres/${sphereId}?${queryParams.toString()}`
        );

        if (response?.sphere) {
          onSphereUpdate(response.sphere);
        }
        return 'read';
      } catch (error) {
        console.warn(`[useSpherePolling] Failed to fetch sphere ${sphereId}:`, error);
        return isTransientReadFailure(error) ? 'transient' : 'refused';
      }
    },
    [apiClient, onSphereUpdate, includeAssignedPerspectives]
  );

  const refreshSpheres = useCallback(
    async (sphereIds: string[]) => {
      // readSphere settles with a verdict and never rejects, so the reads can run together.
      await Promise.all(
        sphereIds.map(async (sphereId) => {
          if ((await readSphere(sphereId)) === 'refused') onSphereRefused?.(sphereId);
        })
      );
    },
    [readSphere, onSphereRefused]
  );

  const pollForUpdates = useCallback(
    (
      sphereIds: string[],
      alwaysPollIds: string[] = [],
      options?: SpherePollingOptions
    ) => {
      if (!apiClient || !sphereIds || sphereIds.length === 0) {
        return;
      }

      const maxAttempts = options?.maxAttempts ?? DEFAULT_MAX_ATTEMPTS;
      const intervalMs = options?.intervalMs ?? DEFAULT_INTERVAL_MS;

      // Filter to only poll spheres that are visible (if visibility check provided)
      // Always include alwaysPollIds (e.g., newly created spheres that may not be in state yet)
      const alwaysPollSet = new Set(alwaysPollIds);
      const spheresToPoll = isNodeVisible
        ? sphereIds.filter((id) => alwaysPollSet.has(id) || isNodeVisible(id))
        : sphereIds;

      if (spheresToPoll.length === 0) {
        console.log('[useSpherePolling] No visible spheres to poll');
        return;
      }

      // Cancel any existing polling
      cancelPolling();
      isPollingRef.current = true;

      console.log('[useSpherePolling] Starting poll for spheres:', spheresToPoll);

      let attempts = 0;

      const poll = async () => {
        if (!isPollingRef.current) {
          console.log('[useSpherePolling] Polling cancelled');
          return;
        }

        attempts++;
        console.log(`[useSpherePolling] Poll attempt ${attempts}/${maxAttempts}`);

        await Promise.all(spheresToPoll.map((sphereId) => readSphere(sphereId)));

        // Schedule next poll if we haven't reached max attempts
        if (attempts < maxAttempts && isPollingRef.current) {
          pollingTimeoutRef.current = setTimeout(poll, intervalMs);
        } else {
          console.log('[useSpherePolling] Polling complete');
          isPollingRef.current = false;
        }
      };

      // Start first poll immediately
      poll();
    },
    [apiClient, isNodeVisible, readSphere, cancelPolling]
  );

  return { pollForUpdates, refreshSpheres, cancelPolling };
}

export default useSpherePolling;
